<template>
  <el-card shadow="never">
    <div slot="header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }"
          >Dashboard</el-breadcrumb-item
        >
        <el-breadcrumb-item
          v-if="facility"
          :to="{ name: 'Facility Communication', path: { facility } }"
        >
          {{
            String($root.state.facilityData.code).toUpperCase()
          }}
          Communications</el-breadcrumb-item
        >
        <el-breadcrumb-item v-else :to="{ name: 'Communication' }"
          >Communications</el-breadcrumb-item
        >
      </el-breadcrumb>
    </div>
    <el-tabs v-model="activeTab">
      <el-tab-pane label="Facility" name="facility">
        <el-card v-loading="loading">
          <div slot="header" class="header">
            <el-button
              :disabled="!validEmail"
              @click="openComposeDialog"
            >
              Compose
            </el-button>
          </div>
        </el-card>
        <el-dialog
          id="compose"
          :visible.sync="composeDialog"
          modal-append-to-body
          destroy-on-close
          width="72%"
          top="2vh"
          @closed="resetComposer"
        >
          <el-steps :active="active" align-center>
            <el-step title="Select Accounts"> </el-step>
            <el-step title="Compose"> </el-step>
          </el-steps>
          <el-card v-if="active === 1">
            <EmailRecipients
              v-if="facility"
              :facility="facility"
              @change="selectAccounts"
            />
            <el-divider></el-divider>
            <el-descriptions>
              <el-descriptions-item label="Include accounts">
                <el-checkbox-group v-model="checkList">
                  <el-checkbox label="MAIN EMAIL" :disabled="true" ></el-checkbox>
                  <el-checkbox label="LEASING"></el-checkbox>
                  <el-checkbox label="WAREHOUSE"></el-checkbox>
                  <el-checkbox label="FINANCE"></el-checkbox>
                  <el-checkbox label="LEGAL"></el-checkbox>
                </el-checkbox-group>
              </el-descriptions-item>
            </el-descriptions>
            <el-button
              :disabled="accounts.length === 0"
              @click="() => active++"
            >
              Next
            </el-button>
          </el-card>
          <el-card v-if="active === 2">
            <el-form label-position="top">
              <el-form-item label="Template">
                <el-select
                  v-model="selectedTemplateId"
                  clearable
                  filterable
                  :loading="templateLoading"
                  placeholder="No template"
                  style="width: 100%;"
                  @change="handleTemplateChange"
                >
                  <el-option
                    v-for="template in publishedTemplates"
                    :key="template.id"
                    :label="getTemplateLabel(template)"
                    :value="template.id"
                  />
                </el-select>
              </el-form-item>
              <el-row :gutter="16">
                <el-col :span="12">
                  <el-form-item label="Header">
                    <el-select
                      v-model="selectedHeaderTemplateId"
                      clearable
                      filterable
                      :disabled="!composerUsesHtmlScaffold"
                      :loading="templateLoading"
                      placeholder="No header"
                      style="width: 100%;"
                    >
                      <el-option
                        v-for="template in headerTemplates"
                        :key="template.id"
                        :label="getTemplateLabel(template)"
                        :value="template.id"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="Footer">
                    <el-select
                      v-model="selectedFooterTemplateId"
                      clearable
                      filterable
                      :disabled="!composerUsesHtmlScaffold"
                      :loading="templateLoading"
                      placeholder="No footer"
                      style="width: 100%;"
                    >
                      <el-option
                        v-for="template in footerTemplates"
                        :key="template.id"
                        :label="getTemplateLabel(template)"
                        :value="template.id"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
            <el-input
              placeholder="Subject"
              v-model="subject"
              required
            ></el-input>
            <el-divider></el-divider>
            <el-row :gutter="20">
              <el-col :span="6">
                <Uploader
                  button-text="Add Attachment"
                  @upload-file="addAttachment"
                  no-tip
                  :upload-prefix="`location/${facility}/`"
                />
              </el-col>
              <el-col :span="6">
                <Uploader
                  :upload-prefix="`location/${facility}/`"
                  list-type="picture"
                  button-text="Upload Image and Copy url"
                  @upload-completed="copyImageUrl"
                  no-tip
                  thumbnail
                />
              </el-col>
              <el-col :span="12">
                <el-button @click="addSignatureToEmail" size="small" type="primary">
                  Add your signature
                </el-button>
              </el-col>
            </el-row>
            <el-divider></el-divider>
            <EmailEditor
              ref="emailEditor"
              v-model="body"
              title="Message body"
              from-label="Sender"
              :context-label="selectedTemplate ? getTemplateLabel(selectedTemplate) : ''"
              :preview="preview"
              :preview-content="composedPreviewBody"
              :from="this.$keycloak.tokenParsed.email"
              :template-properties="selectedTemplateProperties"
              :format="composerFormat"
            />
            <el-descriptions title="Attachments">
              <el-descriptions-item
                v-for="attachment of attachments"
                :key="attachment.url"
                label="Name"
              >
                {{ attachment.name }}
              </el-descriptions-item>
            </el-descriptions>
            <el-divider></el-divider>
            <el-button
              type="info"
              plain
              @click="() => active = active - 1"
            >
              Back
            </el-button>
            <el-button
              v-if="canSendEmail"
              @click="sendEmails"
              v-loading="loading"
            >
              Send
            </el-button>
          </el-card>
        </el-dialog>
      </el-tab-pane>
      <el-tab-pane label="History" name="history">
        <EmailHistory ref="emailHistory" :facility="facility" />
      </el-tab-pane>
      <el-tab-pane label="Templates" name="templates">
        <EmailTemplateManager
          ref="templateManager"
          :facility="facility"
        />
      </el-tab-pane>
      <el-tab-pane label="Variables" name="properties">
        <EmailTemplatePropertyManager @properties-updated="handlePropertyCatalogUpdate" />
      </el-tab-pane>
    </el-tabs>
  </el-card>
</template>

<script>
import axios from 'axios';
import EmailEditor from './EmailEditor';
import EmailTemplateManager from './EmailTemplateManager';
import EmailTemplatePropertyManager from './EmailTemplatePropertyManager';
import EmailRecipients from './EmailRecipients';
import EmailHistory from './EmailHistory';
import Uploader from './Uploader';
import {
  getFacility,
  listEditorEmailTemplateProperties,
  listEditorEmailTemplates,
} from '../utils/commonApi';
import {
  composeHtmlEmailScaffold,
  getEmailTemplateContent,
} from '../utils/emailScaffold';
import {
  CUSTOMER_API_BASE,
  EMAIL_API_BASE,
  FACILITY_ACCOUNT_API_BASE,
  LEASING_API,
} from '../env';

const COMPOSER_CONTEXT_SOURCE_MODELS = ['customer', 'facility', 'account', 'lease'];

const RECORD_SOURCE_MODEL_LABELS = {
  invoice: 'Invoice',
  transaction: 'Transaction',
  lease: 'Lease',
  unit: 'Unit',
  payment: 'Payment',
  refund: 'Refund',
  transfer: 'Transfer',
  moveout: 'Move-out',
  event: 'Event',
};

const DEFAULT_FACILITY_ACCOUNT = {
  wallet: 0,
  amount: 0,
  amountPaid: 0,
  deposit: 0,
  depositToBeRefunded: 0,
};

function normalizeIdValue(value) {
  if (!value) {
    return '';
  }

  if (typeof value === 'object') {
    const { $oid: oid, id, _id: objectId } = value;

    return String(oid || id || objectId || '');
  }

  return String(value);
}

function normalizeTemplateType(type) {
  return String(type || 'general').trim().toLowerCase() || 'general';
}

function normalizeTemplateStatus(status) {
  return String(status || 'draft').trim().toLowerCase() || 'draft';
}

function normalizeTemplateFormat(format) {
  return String(format || 'html').trim().toLowerCase() || 'html';
}

function normalizeComposerTemplate(template) {
  const { _id: objectId } = template;
  const templateId = template.id || objectId || '';

  return {
    ...template,
    id: normalizeIdValue(templateId),
    type: normalizeTemplateType(template.type),
    status: normalizeTemplateStatus(template.status),
    format: normalizeTemplateFormat(template.format),
    version: template.version || 1,
    body: getEmailTemplateContent(template),
  };
}

function normalizeComposerTemplateProperty(property) {
  if (!property || typeof property !== 'object') {
    return {
      id: '',
      key: String(property || '').trim().toLowerCase(),
      sourceModel: 'manual',
      sourcePath: '',
    };
  }

  const { _id: objectId } = property;
  return {
    ...property,
    id: property.id || objectId || '',
    key: String(property.key || '').trim().toLowerCase(),
    sourceModel: String(property.sourceModel || 'manual').trim().toLowerCase(),
    sourcePath: property.sourcePath || '',
  };
}

function normalizeRecordList(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (!data || typeof data !== 'object') {
    return [];
  }

  if (Array.isArray(data.result)) {
    return data.result;
  }

  if (Array.isArray(data.data)) {
    return data.data;
  }

  return [];
}

function pickActiveLease(leases) {
  if (!Array.isArray(leases) || !leases.length) {
    return null;
  }

  return leases.find(lease => lease && !lease.future && !lease.completed && lease.leaseStarted)
    || leases.find(lease => lease && !lease.future)
    || null;
}

export default {
  name: 'CommunicationPage',
  components: {
    EmailEditor,
    EmailTemplateManager,
    EmailTemplatePropertyManager,
    EmailRecipients,
    EmailHistory,
    Uploader,
  },
  props: {
    facility: { type: String },
  },
  watch: {
    async facility() {
      await this.getFacilityData();
    },
  },
  computed: {
    modifiedCheckList() {
      return this.checkList.filter(item => item !== 'MAIN EMAIL');
    },
    validEmail() {
      return (
        this.facilityData.email &&
        this.facilityData.email.endsWith('readyspaces.com')
      );
    },
    publishedTemplates() {
      return this.emailTemplates.filter(template =>
        template.status === 'published' &&
        template.type !== 'header' &&
        template.type !== 'footer');
    },
    headerTemplates() {
      return this.emailTemplates.filter(template =>
        template.status === 'published' &&
        template.type === 'header' &&
        template.format === 'html');
    },
    footerTemplates() {
      return this.emailTemplates.filter(template =>
        template.status === 'published' &&
        template.type === 'footer' &&
        template.format === 'html');
    },
    selectedTemplate() {
      return this.getTemplateById(this.selectedTemplateId);
    },
    selectedHeaderTemplate() {
      return this.getTemplateById(this.selectedHeaderTemplateId);
    },
    selectedFooterTemplate() {
      return this.getTemplateById(this.selectedFooterTemplateId);
    },
    isManagedTemplateSelected() {
      return !!this.selectedTemplateId;
    },
    canSendEmail() {
      if (this.isManagedTemplateSelected) {
        return !!this.selectedTemplate &&
          !!this.subject &&
          !!this.body;
      }

      return !!this.body && !!this.subject;
    },
    composerFormat() {
      return this.selectedTemplate && this.selectedTemplate.format ? this.selectedTemplate.format : 'html';
    },
    composerUsesHtmlScaffold() {
      return String(this.composerFormat || 'html').toLowerCase() === 'html';
    },
    composedPreviewBody() {
      return this.getComposedBody();
    },
    selectedTemplateProperties() {
      if (!this.isManagedTemplateSelected) {
        return [];
      }

      if (Array.isArray(this.emailTemplateProperties) && this.emailTemplateProperties.length) {
        return this.emailTemplateProperties;
      }

      if (!this.selectedTemplate || !Array.isArray(this.selectedTemplate.properties)) {
        return [];
      }

      return this.selectedTemplate.properties.map(normalizeComposerTemplateProperty);
    },
  },
  async mounted() {
    await this.getFacilityData();
  },
  data() {
    return {
      loading: false,
      composeDialog: false,
      preview: true,
      activeTab: 'facility',
      facilityData: {
        email: '',
        code: '',
      },
      accounts: [],
      checkList: ['MAIN EMAIL'],
      attachments: [],
      active: 1,
      subject: '',
      body: '',
      emailTemplates: [],
      emailTemplateProperties: [],
      selectedTemplateId: '',
      selectedHeaderTemplateId: '',
      selectedFooterTemplateId: '',
      templateLoading: false,
    };
  },
  methods: {
    getAuthHeaders() {
      return {
        Authorization: `Bearer ${this.$keycloak.token}`,
      };
    },
    extractTemplatePropertyKeys() {
      const keys = [];
      const seenKeys = new Set();
      const tokenPattern = /{{{\s*([^{}]+?)\s*}}}|{{\s*([^{}]+?)\s*}}/g;

      [this.subject, this.getComposedBody()].forEach((part) => {
        if (typeof part !== 'string' || !part.length) {
          return;
        }

        let match = tokenPattern.exec(part);

        while (match) {
          const key = String(match[1] || match[2] || '').trim().toLowerCase();
          if (key && !seenKeys.has(key)) {
            seenKeys.add(key);
            keys.push(key);
          }
          match = tokenPattern.exec(part);
        }

        tokenPattern.lastIndex = 0;
      });

      return keys;
    },
    getTemplatePropertyMap() {
      const propertyMap = new Map();
      const addProperty = (property) => {
        const normalizedProperty = normalizeComposerTemplateProperty(property);

        if (normalizedProperty.key) {
          propertyMap.set(normalizedProperty.key, normalizedProperty);
        }
      };

      if (this.selectedTemplate && Array.isArray(this.selectedTemplate.properties)) {
        this.selectedTemplate.properties.forEach(addProperty);
      }

      if (Array.isArray(this.emailTemplateProperties)) {
        this.emailTemplateProperties.forEach(addProperty);
      }

      return propertyMap;
    },
    getCurrentTemplateProperties() {
      const propertyMap = this.getTemplatePropertyMap();

      return this.extractTemplatePropertyKeys()
        .map(key => propertyMap.get(key))
        .filter(Boolean);
    },
    getCurrentTemplateSourceModels() {
      const sourceModels = this.getCurrentTemplateProperties()
        .map(property => property.sourceModel)
        .filter(sourceModel =>
          sourceModel
          && sourceModel !== 'manual'
          && (
            COMPOSER_CONTEXT_SOURCE_MODELS.includes(sourceModel)
            || Object.prototype.hasOwnProperty.call(RECORD_SOURCE_MODEL_LABELS, sourceModel)
          ));

      return Array.from(new Set(sourceModels));
    },
    getUnsupportedTemplateSourceModels() {
      return this.getCurrentTemplateSourceModels()
        .filter(sourceModel => !COMPOSER_CONTEXT_SOURCE_MODELS.includes(sourceModel));
    },
    getComposerTemplateContextSupportError() {
      const unsupportedSourceModels = this.getUnsupportedTemplateSourceModels();

      if (!unsupportedSourceModels.length) {
        return '';
      }

      const labels = unsupportedSourceModels
        .map(sourceModel => RECORD_SOURCE_MODEL_LABELS[sourceModel] || sourceModel)
        .join(', ');

      return `This composer can only auto-fill facility, customer, account, and active lease variables. ${labels} variables must be sent from the related workflow so the exact record is known.`;
    },
    async openComposeDialog() {
      this.composeDialog = true;
      await this.loadComposerTemplates();
    },
    resetComposer() {
      this.attachments = [];
      this.accounts = [];
      this.body = '';
      this.subject = '';
      this.active = 1;
      this.checkList = ['MAIN EMAIL'];
      this.selectedTemplateId = '';
      this.selectedHeaderTemplateId = '';
      this.selectedFooterTemplateId = '';
    },
    getTemplateLabel(template) {
      if (!template) {
        return '';
      }

      return `${template.name || template.key} (v${template.version || 1})`;
    },
    getTemplateById(templateId) {
      if (!templateId) {
        return null;
      }

      const selectedId = normalizeIdValue(templateId);

      return this.emailTemplates.find((template) => {
        const { id, _id: objectId } = template;

        return normalizeIdValue(id || objectId) === selectedId;
      }) || null;
    },
    getSelectedComponentBody(template) {
      return template ? getEmailTemplateContent(template) : '';
    },
    getComposedBody() {
      if (!this.composerUsesHtmlScaffold) {
        return this.body;
      }

      return composeHtmlEmailScaffold({
        header: this.getSelectedComponentBody(this.selectedHeaderTemplate),
        body: this.body,
        footer: this.getSelectedComponentBody(this.selectedFooterTemplate),
      });
    },
    async loadComposerTemplates() {
      try {
        this.templateLoading = true;
        const [templates, properties] = await Promise.all([
          listEditorEmailTemplates(this.$keycloak.token, {
            status: 'published',
          }),
          listEditorEmailTemplateProperties(this.$keycloak.token, {
            status: 'active',
          }),
        ]);
        this.emailTemplates = templates.map(normalizeComposerTemplate);
        this.emailTemplateProperties = properties
          .map(normalizeComposerTemplateProperty)
          .filter(property => property.key);
      } catch (err) {
        this.$message.error({
          title: 'Unable to load templates',
          message: err.message,
        });
      } finally {
        this.templateLoading = false;
      }
    },
    async handleTemplateChange(templateId) {
      if (!templateId) {
        this.subject = '';
        this.body = '';
        return;
      }

      const template = this.getTemplateById(templateId);

      if (!template) {
        this.$message.warning({
          title: 'Template unavailable',
          message: 'Selected template could not be loaded. Please refresh templates and try again.',
        });
        return;
      }

      this.selectedTemplateId = template.id;
      this.subject = template.subject || '';
      this.body = template.body || '';
    },
    async copyImageUrl(url) {
      await navigator.clipboard.writeText(url);
      this.$message.success({
        title: 'Copied to clipboard',
        message: 'Uploaded image url copied to clipboard',
      });
    },
    async getFacilityData() {
      this.loading = true;
      const facilityData = await getFacility(this.facility, {
        token: this.$keycloak.token,
      });
      this.facilityData = facilityData;
      if (!this.validEmail) {
        this.$message.warning({
          title: 'Invalid Facility Email',
          message: 'To Compose emails, a facility needs to have a valid email.',
        });
      }
      this.loading = false;
    },
    async handlePropertyCatalogUpdate() {
      if (
        this.$refs.templateManager &&
        typeof this.$refs.templateManager.loadProperties === 'function'
      ) {
        await this.$refs.templateManager.loadProperties();
      }

      if (this.composeDialog) {
        await this.loadComposerTemplates();
      }
    },
    selectAccounts(accounts) {
      this.accounts = accounts;
    },
    addSignatureToEmail() {
      if (!this.$refs.emailEditor || typeof this.$refs.emailEditor.insertSignature !== 'function') {
        this.$message.error({
          title: 'Error',
          message: 'Editor is not ready yet.',
        });
        return;
      }
      this.$refs.emailEditor.insertSignature();
    },
    routeToDashboard() {
      if (this.facility) {
        this.$router.push({
          name: 'Facility Dashboard',
          params: { facility: this.facility },
        });
        return;
      }
      this.$router.push({ name: 'Dashboard' });
    },
    addAttachment(file) {
      this.attachments.push({
        name: file.raw.name,
        type: file.raw.type || 'unknown',
        url: file.response.url,
      });
    },
    async refreshEmailHistory() {
      if (
        this.$refs.emailHistory &&
        typeof this.$refs.emailHistory.fetchEmails === 'function'
      ) {
        await this.$refs.emailHistory.fetchEmails();
      }
    },
    getCustomerContextKey(customer) {
      if (!customer || typeof customer !== 'object') {
        return '';
      }

      const { _id: objectId } = customer;
      const key = objectId || customer.id || customer.customerId || customer.email || '';

      return key ? String(key) : '';
    },
    getFacilityTemplateContext() {
      const facilityData = this.facilityData && typeof this.facilityData === 'object'
        ? this.facilityData
        : {};
      const { _id: objectId } = facilityData;
      const selectedFacilityId = this.facility ? String(this.facility) : '';
      const resolvedFacilityId = objectId
        || facilityData.id
        || facilityData.facilityId
        || selectedFacilityId;

      return {
        ...facilityData,
        _id: objectId || resolvedFacilityId,
        id: facilityData.id || resolvedFacilityId,
        facilityId: facilityData.facilityId || resolvedFacilityId,
        selectedFacilityId: selectedFacilityId || resolvedFacilityId,
      };
    },
    getCustomerAccountContext(customer) {
      if (!customer || typeof customer !== 'object' || !Array.isArray(customer.accounts)) {
        return {};
      }

      const facilityId = String(this.facility || '');
      const account = customer.accounts.find(item =>
        item && Array.isArray(item.locations) && item.locations.map(String).includes(facilityId));

      return account || customer.accounts[0] || {};
    },
    async fetchFacilityAccount(customerId) {
      if (!this.facility || !customerId) {
        throw new Error('Facility account context requires a facility and customer.');
      }

      try {
        const response = await axios.get(
          `${FACILITY_ACCOUNT_API_BASE}/v1/${this.facility}/${customerId}`,
          {
            headers: this.getAuthHeaders(),
          },
        );
        const facilityAccount = response.data && typeof response.data === 'object'
          ? response.data
          : {};
        const accountContext = {
          ...DEFAULT_FACILITY_ACCOUNT,
          ...facilityAccount,
        };

        if (
          (accountContext.wallet === undefined || accountContext.wallet === null)
          && (accountContext.amount || accountContext.amountPaid)
        ) {
          accountContext.wallet = accountContext.amountPaid - accountContext.amount;
        }

        return accountContext;
      } catch (err) {
        throw new Error(
          `Unable to load facility account context for customer ${customerId}: ${err.message}`,
        );
      }
    },
    async fetchCustomerLeases(customerId) {
      if (!this.facility || !customerId) {
        throw new Error('Lease context requires a facility and customer.');
      }

      try {
        const response = await axios.get(
          `${LEASING_API}/v1/customer/${customerId}/leases`,
          {
            params: {
              facility: this.facility,
            },
            headers: this.getAuthHeaders(),
          },
        );

        return normalizeRecordList(response.data);
      } catch (err) {
        throw new Error(
          `Unable to load lease context for customer ${customerId}: ${err.message}`,
        );
      }
    },
    async fetchActiveLeaseContext(customerId) {
      const leases = await this.fetchCustomerLeases(customerId);
      const activeLease = pickActiveLease(leases);

      if (!activeLease) {
        throw new Error(`No active lease found for customer ${customerId} in selected facility.`);
      }

      return {
        ...activeLease,
        leases,
      };
    },
    async buildTemplateContextForCustomer(customer, sourceModels) {
      const context = {
        customer,
      };
      const customerId = this.getCustomerContextKey(customer);
      const sourceModelSet = new Set(sourceModels);
      const requests = [];

      if (sourceModelSet.has('account')) {
        requests.push(this.fetchFacilityAccount(customerId).then((account) => {
          context.account = account;
        }));
        context.customerAccount = this.getCustomerAccountContext(customer);
      }

      if (sourceModelSet.has('lease')) {
        requests.push(this.fetchActiveLeaseContext(customerId).then((lease) => {
          context.lease = lease;
        }));
      }

      await Promise.all(requests);

      return context;
    },
    async buildCustomerTemplateContext(customers) {
      if (!Array.isArray(customers)) {
        return {};
      }

      const sourceModels = this.getCurrentTemplateSourceModels();
      const customerContexts = await Promise.all(customers.map(customer =>
        this.buildTemplateContextForCustomer(customer, sourceModels)));

      return customers.reduce((context, customer, index) => {
        const key = this.getCustomerContextKey(customer);

        if (!key) {
          return context;
        }

        return {
          ...context,
          [key]: customerContexts[index],
        };
      }, {});
    },
    async sendEmails() {
      this.loading = true;
      try {
        const templateContextError = this.isManagedTemplateSelected
          ? this.getComposerTemplateContextSupportError()
          : '';

        if (templateContextError) {
          this.$message.error({
            title: 'Template needs record context',
            message: templateContextError,
          });
          return;
        }

        const response = await axios.get(`${CUSTOMER_API_BASE}/customers/v1/get/all`, {
          params: {
            customers: [...this.accounts].toString(),
            limit: this.accounts.length,
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        const customers = response.data;
        const endpoint = this.isManagedTemplateSelected
          ? `${EMAIL_API_BASE}/send/email/group/v2`
          : `${EMAIL_API_BASE}/send/email/group`;
        const facilityContext = this.getFacilityTemplateContext();
        const payload = this.isManagedTemplateSelected
          ? {
            customers,
            accounts: this.modifiedCheckList,
            from: this.$keycloak.tokenParsed.email,
            templateId: this.selectedTemplateId,
            subject: this.subject,
            header: '',
            body: this.getComposedBody(),
            footer: '',
            format: this.composerFormat,
            propertyKeys: this.extractTemplatePropertyKeys(),
            templateContext: {
              facility: facilityContext,
              facilityId: facilityContext.facilityId,
              selectedFacilityId: facilityContext.selectedFacilityId,
            },
            templateContextByCustomerId: await this.buildCustomerTemplateContext(customers),
            attachments: this.attachments,
            facility: this.facility,
          }
          : {
            customers,
            accounts: this.modifiedCheckList,
            body: this.getComposedBody(),
            from: this.$keycloak.tokenParsed.email,
            subject: this.subject,
            attachments: this.attachments,
            facility: this.facility,
          };

        await axios.post(
          endpoint,
          payload,
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );

        await this.refreshEmailHistory();
        this.resetComposer();
        this.composeDialog = false;
      } catch (err) {
        const errorMessage = err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : err.message;
        this.$message.error({
          title: 'Unable to send email',
          message: errorMessage,
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/CommunicationPage.vue